/**
 * PRD registry endpoints — all URLs include trailing slash to avoid FastAPI
 * 307 redirects. `create` is included to keep the wire-contract surface
 * complete (used by tests and the slash-command integration). The dashboard
 * UI does not expose a create button — PRD creation is agent-driven, not
 * UI-initiated.
 */

import type {
  PRD,
  PRDBatchResponse,
  PRDBodyRevisionListResponse,
  PRDBodyRevisionResponse,
  PRDBodyUpdate,
  PRDClaim,
  PRDCreate,
  PRDListFilters,
  PRDListResponse,
  PRDStatusTransition,
} from '@/types/prd'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'
import { batchIdsQuery, repeatedKeyParamsSerializer } from './queryParams'

export const prds = {
  list: async (filters?: PRDListFilters): Promise<PRDListResponse> => {
    const response = await apiClient.get<PRDListResponse>('/api/v1/prds/', {
      params: filters,
      paramsSerializer: repeatedKeyParamsSerializer,
    })
    return response.data
  },

  getById: async (id: string): Promise<PRD> => {
    const response = await apiClient.get<PRD>(`/api/v1/prds/${id}/`)
    return response.data
  },

  getByNumber: async (n: number): Promise<PRD> => {
    const response = await apiClient.get<PRD>(`/api/v1/prds/by-number/${n}/`)
    return response.data
  },

  batch: async (ids: string[]): Promise<PRDBatchResponse> => {
    const response = await apiClient.get<PRDBatchResponse>(
      `/api/v1/prds/batch/?${batchIdsQuery(ids)}`
    )
    return response.data
  },

  create: async (data: PRDCreate): Promise<PRD> => {
    const response = await apiClient.post<PRD>('/api/v1/prds/', data)
    return response.data
  },

  // `ifMatch` is REQUIRED (not optional) — dashboard mutations always send
  // the current metadata_version to guard against concurrent edits (412 on conflict).
  claim: async (id: string, data: PRDClaim, ifMatch: number): Promise<PRD> => {
    const response = await apiClient.post<PRD>(
      `/api/v1/prds/${id}/claim/`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  transitionStatus: async (
    id: string,
    data: PRDStatusTransition,
    ifMatch: number
  ): Promise<PRD> => {
    const response = await apiClient.put<PRD>(
      `/api/v1/prds/${id}/status/`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  delete: async (id: string, ifMatch: number): Promise<void> => {
    await apiClient.delete(`/api/v1/prds/${id}/`, ifMatchHeaders(ifMatch))
  },

  // `ifMatch` is REQUIRED — updateBody guards against concurrent edits the
  // same way claim/transitionStatus do; 412 on version mismatch.
  updateBody: async (
    id: string,
    data: PRDBodyUpdate,
    ifMatch: number
  ): Promise<PRD> => {
    const response = await apiClient.patch<PRD>(
      `/api/v1/prds/${id}/body/`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  listRevisions: async (
    id: string,
    opts: { skip?: number; limit?: number } = {}
  ): Promise<PRDBodyRevisionListResponse> => {
    const response = await apiClient.get<PRDBodyRevisionListResponse>(
      `/api/v1/prds/${id}/revisions/`,
      { params: { skip: opts.skip ?? 0, limit: opts.limit ?? 50 } }
    )
    return response.data
  },

  getRevision: async (
    id: string,
    revisionId: string
  ): Promise<PRDBodyRevisionResponse> => {
    const response = await apiClient.get<PRDBodyRevisionResponse>(
      `/api/v1/prds/${id}/revisions/${revisionId}/`
    )
    return response.data
  },
}
